<script lang="ts">
  import { untrack } from 'svelte';
  import { brandStore } from '@/lib/brandStore.svelte';
  import { session } from '@/lib/session.svelte';
  import { navigateTo } from '@/lib/router';
  import { playDetectionSound, playTapSound, vibrateDetection, vibrateConfirmed, isAudioEnabled, setAudioEnabled } from '@/lib/feedback.svelte';
  import { DetectionTracker } from '@/lib/detectionTracker';
  import ArScanLine from './ArScanLine.svelte';
  import ArCornerBrackets from './ArCornerBrackets.svelte';
  import ArDataTag from './ArDataTag.svelte';

  declare const OnnxInference: any;
  declare const ArCamera: any;

  let { onResult }: { onResult: (category: string, confidence: number) => void } = $props();

  const labels = $derived(brandStore.labels);
  const arConfig = $derived(brandStore.arConfig);

  type ScanPhase = 'idle' | 'scanning' | 'detected' | 'selected';

  interface ZoneDetection {
    className: string;
    confidence: number;
    svgX: number; svgY: number; svgW: number; svgH: number;
    zoneIndex: number;
  }

  let brightness = $state(0);
  let lightLevel = $derived(Math.min(5, Math.max(0, Math.round(brightness * 5))));
  let arStatus = $state<'idle' | 'loading' | 'ready' | 'scanning' | 'found' | 'error'>('idle');
  let currentDetection = $state<{ category: string; confidence: number } | null>(null);
  let allScores = $state<Record<string, number>>({});
  let guidanceText = $state('Carregando modelo de IA...');
  let isCapturing = $state(false);
  let showFallbackWarning = $state(false);
  let modelReady = $state(false);
  let audioEnabled = $state(true);
  let staleMap = $state<Record<number, boolean>>({});
  let showAutoCaptureOverlay = $state(false);
  let autoCaptureCountdown = $state(0);
  let autoCaptureTimer: ReturnType<typeof setInterval> | null = null;
  let dbgFrames = $state(0);
  let dbgLastRaw = $state('');
  let dbgPipelineState = $state('init');

  let scanPhase = $state<ScanPhase>('idle');
  let selectedZoneIndex = $state<number | null>(null);
  let selectedZone = $derived(selectedZoneIndex !== null ? detectionZones[selectedZoneIndex] ?? null : null);
  let noDetectionCountdown = $state(0);
  let noDetectionTimer: ReturnType<typeof setTimeout> | null = null;
  let scanInterval: ReturnType<typeof setInterval> | null = null;
  let brightLoop: ReturnType<typeof setInterval> | null = null;
  let autoSelectTimer: ReturnType<typeof setTimeout> | null = null;

  let topScores = $derived(
    Object.entries(allScores)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
  );

  function scoreColor(conf: number): string {
    if (conf >= 0.7) return 'high';
    if (conf >= 0.4) return 'medium';
    return 'low';
  }

  function zoneStale(zoneIndex: number): boolean {
    return !!staleMap[zoneIndex];
  }

  const highlightIntensity = $derived(
    currentDetection ? Math.min(1, currentDetection.confidence) :
    detectionZones.length > 0 ? Math.min(1, detectionZones.length * 0.3) : 0
  );

  let detectionZones = $state<ZoneDetection[]>([]);
  const tracker = new DetectionTracker(5, 3000);

  $effect(() => {
    console.log('[ArScreen] $effect check: modelReady:', modelReady, 'arStatus:', arStatus, 'scanPhase:', scanPhase);
    if (modelReady && (arStatus === 'ready' || arStatus === 'idle') && untrack(() => scanPhase) === 'idle') {
      scanPhase = 'scanning';
      guidanceText = 'Procurando peça...';
      const hasOnnx = typeof OnnxInference !== 'undefined';
      const hasCam = typeof ArCamera !== 'undefined';
      dbgPipelineState = 'onnx:' + hasOnnx + ' cam:' + hasCam;
      if (!hasOnnx || !hasCam) { dbgPipelineState = 'ERROR: missing globals'; return; }

      OnnxInference.startDetection(
        function onStabilized(result: any) {
          console.log('[ArScreen] onStabilized:', result.className, (result.confidence * 100).toFixed(1) + '%');
          if (!result.className) return;

          if (scanPhase === 'scanning') {
            if (isAudioEnabled()) playDetectionSound();
            vibrateDetection();
          }
          scanPhase = 'detected';

          if (result.confidence >= 0.75) {
            guidanceText = 'Detectado com alta confiança! Capturando...';
            autoCaptureFromAr();
          } else if (result.confidence >= 0.5) {
            guidanceText = `${brandStore.translateCategory(result.className)} (${(result.confidence * 100).toFixed(0)}%) — mantenha firme`;
          } else {
            guidanceText = 'Aproxime ou reposicione...';
          }
        },
        function onFrame(result: any) {
          dbgFrames++;
          dbgLastRaw = (result.className || 'null') + ' ' + ((result.confidence ?? 0) * 100).toFixed(0) + '%';
          dbgPipelineState = 'frame:' + dbgFrames;
          allScores = result.allScores ?? {};
          currentDetection = result.className
            ? { category: result.className, confidence: result.confidence }
            : null;

          if (result.className && result.confidence >= 0.3) {
            const viewport = document.querySelector('.ar-viewport');
            const vw = viewport?.clientWidth ?? 300;
            const vh = viewport?.clientHeight ?? 200;
            const zone: ZoneDetection = {
              className: result.className,
              confidence: result.confidence,
              svgX: vw * 0.1,
              svgY: vh * 0.1,
              svgW: vw * 0.8,
              svgH: vh * 0.8,
              zoneIndex: 0,
            };
            tracker.update([zone]);
            detectionZones = tracker.getActiveZones();
            const fresh = tracker.getFreshZones();
            staleMap = Object.fromEntries(detectionZones.map(z => [z.zoneIndex, !fresh.some((f: any) => f.zoneIndex === z.zoneIndex)]));
            console.log('[ArScreen:onFrame] Zone created:', result.className, 'activeZones:', detectionZones.length, 'scanPhase:', scanPhase);
          } else {
            tracker.update([]);
            detectionZones = tracker.getActiveZones();
            if (detectionZones.length === 0 && scanPhase === 'detected') {
              console.log('[ArScreen:onFrame] Lost detection, back to scanning');
              scanPhase = 'scanning';
              guidanceText = 'Procurando peça...';
              selectedZoneIndex = null;
            }
          }
        }
      );

      setTimeout(() => {
        const v = document.getElementById('camera-video') as HTMLVideoElement;
        const c = document.getElementById('detection-canvas') as HTMLCanvasElement;
        dbgPipelineState += ' vid:' + (v ? v.videoWidth + 'x' + v.videoHeight + (v.paused ? ':paused' : ':playing') : 'null');
        dbgPipelineState += ' cvs:' + (c ? 'ok' : 'null');
        dbgPipelineState += ' stream:' + (v?.srcObject ? 'yes' : 'no');
      }, 2000);

      brightLoop = setInterval(() => {
        const imgData = ArCamera.getImageData?.();
        if (imgData) brightness = computeBrightness(imgData);
      }, 500);

      return () => {
        console.log('[ArScreen] Cleanup: stopping detection');
        OnnxInference.stopDetection();
        if (brightLoop) { clearInterval(brightLoop); brightLoop = null; }
      };
    }
  });

  function computeBrightness(imageData: ImageData): number {
    const data = imageData.data;
    let sum = 0;
    for (let i = 0; i < data.length; i += 4) {
      sum += data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114;
    }
    return sum / (data.length / 4) / 255;
  }

  function stopScanLoop() {
    OnnxInference.stopDetection();
    if (brightLoop) { clearInterval(brightLoop); brightLoop = null; }
    if (autoSelectTimer) { clearTimeout(autoSelectTimer); autoSelectTimer = null; }
    showAutoCaptureOverlay = false;
  }

  function autoCaptureFromAr() {
    console.log('[ArScreen] autoCaptureFromAr() currentDetection:', currentDetection);
    const video = document.getElementById('camera-video') as HTMLVideoElement;
    if (!video || !video.videoWidth || !currentDetection) { console.warn('[ArScreen] autoCaptureFromAr() aborted: video:', !!video, 'width:', video?.videoWidth, 'detection:', !!currentDetection); return; }
    isCapturing = true;
    setTimeout(() => {
      const c = document.createElement('canvas');
      c.width = video.videoWidth;
      c.height = video.videoHeight;
      c.getContext('2d')!.drawImage(video, 0, 0);
      c.toBlob(async (blob) => {
        if (!blob) { isCapturing = false; return; }
        const file = new File([blob], 'ar-auto.jpg', { type: 'image/jpeg' });
        session.setFile(file);

        const formData = new FormData();
        formData.append('image', file);
        const v = session.vehicle;
        if (v?.make) formData.append('vehicle_make', v.make);
        if (v?.model) formData.append('vehicle_model', v.model);
        if (v?.year) formData.append('vehicle_year', v.year);

        try {
          const res = await fetch('/predict', { method: 'POST', body: formData });
          if (!res.ok) throw new Error('HTTP ' + res.status);
          const data = await res.json();
          const hasMatches = data.matches && data.matches.length > 0;
          session.setResults({
            category: data.product_id ?? data.category ?? currentDetection!.category,
            confidence: data.confidence ?? currentDetection!.confidence,
            scores: data.all_scores ?? {},
            matches: hasMatches ? (data.matches ?? []).map((m: Record<string, unknown>) => ({
              id: m.id ?? '',
              category: m.category ?? '',
              name: m.title ?? '',
              imageUrl: m.image_url,
              pricePix: m.price,
              priceInstallment: m.price,
              installmentCount: 6,
              sourceUrl: m.link,
            })) : [],
          });
        } catch {
          session.setResults({ category: currentDetection!.category, confidence: currentDetection!.confidence, scores: {}, matches: [] });
        }
        isCapturing = false;
        stopScanLoop();
        navigateTo('results');
      }, 'image/jpeg', 0.9);
    }, 300);
  }

  function toggleScan() {
    if (scanPhase === 'scanning' || scanPhase === 'detected') {
      stopScanLoop();
      scanPhase = 'idle';
      currentDetection = null;
      guidanceText = labels?.arGuidanceReady ?? 'Modelo pronto. Aponte para a peça.';
      return;
    }
    stopScanLoop();
    currentDetection = null;
    scanPhase = 'idle';
    modelReady = true;
  }

  function handleZonesSvgClick(event: MouseEvent) {
    const rect = (event.currentTarget as SVGElement).getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const clickY = event.clientY - rect.top;
    for (let i = 0; i < detectionZones.length; i++) {
      const z = detectionZones[i];
      if (clickX >= z.svgX && clickX <= z.svgX + z.svgW &&
          clickY >= z.svgY && clickY <= z.svgY + z.svgH) {
        autoSelectTimer && clearTimeout(autoSelectTimer);
        autoSelectTimer = null;
        selectedZoneIndex = i;
        scanPhase = 'selected';
        playTapSound();
        vibrateConfirmed();
        guidanceText = `${brandStore.translateCategory(z.className)} (${(z.confidence * 100).toFixed(0)}%) - escolha uma ação`;
        return;
      }
    }
  }

  function selectZoneFromSheet(i: number) {
    autoSelectTimer && clearTimeout(autoSelectTimer);
    autoSelectTimer = null;
    selectedZoneIndex = i;
    scanPhase = 'selected';
    playTapSound();
    vibrateConfirmed();
    const zone = detectionZones[i];
    guidanceText = `${brandStore.translateCategory(zone.className)} (${(zone.confidence * 100).toFixed(0)}%) - escolha uma ação`;
  }

  function chooseAnother() {
    stopScanLoop();
    currentDetection = null;
    scanPhase = 'idle';
    modelReady = true;
  }

  async function goToPart() {
    const zone = selectedZone;
    if (!zone) return;
    isCapturing = true;
    const video = document.getElementById('camera-video') as HTMLVideoElement;
    if (!video) { isCapturing = false; return; }
    const c = document.createElement('canvas');
    c.width = video.videoWidth;
    c.height = video.videoHeight;
    c.getContext('2d')!.drawImage(video, 0, 0);
    const blob = await new Promise<Blob | null>(r => c.toBlob(r, 'image/jpeg', 0.9));
    if (!blob) { isCapturing = false; return; }
    const file = new File([blob], 'ar-piece.jpg', { type: 'image/jpeg' });
    session.setFile(file);

    const formData = new FormData();
    formData.append('image', file);
    const v = session.vehicle;
    if (v?.make) formData.append('vehicle_make', v.make);
    if (v?.model) formData.append('vehicle_model', v.model);
    if (v?.year) formData.append('vehicle_year', v.year);

    try {
      const res = await fetch('/predict', { method: 'POST', body: formData });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const data = await res.json();
      const hasMatches = data.matches && data.matches.length > 0;
      session.setResults({
        category: data.product_id ?? data.category ?? zone.className,
        confidence: data.confidence ?? zone.confidence,
        scores: data.all_scores ?? {},
        matches: hasMatches ? (data.matches ?? []).map((m: Record<string, unknown>) => ({
          id: m.id ?? '',
          category: m.category ?? '',
          name: m.title ?? '',
          imageUrl: m.image_url,
          pricePix: m.price,
          priceInstallment: m.price,
          installmentCount: 6,
          sourceUrl: m.link,
        })) : [],
      });
      if (hasMatches) {
        window.ArOverlay?.showSuccess('Produto encontrado!');
      } else {
        window.ArOverlay?.showInfo('Nenhum produto encontrado. Tente outra imagem.');
      }
    } catch (err: any) {
      if (err?.message === 'Failed to fetch' || err?.message?.startsWith('HTTP')) {
        window.ArOverlay?.showWarning('Modo offline: usando resultado local.');
      }
      session.setResults({ category: zone.className, confidence: zone.confidence, scores: {}, matches: [] });
    }
    isCapturing = false;
    stopScanLoop();
    navigateTo('results');
  }

  async function startArMode() {
    console.log('[ArScreen] startArMode() called');
    arStatus = 'loading';
    guidanceText = labels?.arGuidanceLoading ?? 'Carregando modelo de IA...';

    try {
      console.log('[ArScreen] Calling ArCamera.init()');
      ArCamera.init();
      console.log('[ArScreen] Calling ArCamera.startCamera()');
      await ArCamera.startCamera();
      console.log('[ArScreen] Camera started successfully');
      const videoEl = document.getElementById('camera-video') as HTMLVideoElement;
      if (videoEl) videoEl.classList.add('active');
    } catch (err: any) {
      arStatus = 'error';
      if (err?.name === 'NotFoundError' || err?.name === 'DevicesNotFoundError') {
        guidanceText = 'Nenhuma camera detectada neste dispositivo.';
        window.ArOverlay?.showError('camera_not_found');
      } else if (err?.name === 'NotAllowedError' || err?.name === 'PermissionDeniedError') {
        guidanceText = 'Acesso a camera negado. Verifique as permissoes do navegador.';
        window.ArOverlay?.showError('camera_denied');
      } else if (err?.name === 'NotReadableError' || err?.name === 'TrackStartError') {
        guidanceText = 'Camera em uso por outro aplicativo.';
        window.ArOverlay?.showError('camera_in_use');
      } else {
        guidanceText = labels?.arGuidanceError ?? 'Erro de conexao. Use Capturar.';
        window.ArOverlay?.showError('camera_denied', err?.message ?? 'Erro desconhecido ao iniciar camera');
      }
      return;
    }

    try {
      await OnnxInference.loadModel(1, {
        onProgress(info: any) {
          if (info.stage === 'downloading') {
            guidanceText = (labels?.arGuidanceDownloading ?? 'Baixando modelo') + ': ' + info.percent + '%';
          } else if (info.stage === 'initializing') {
            guidanceText = labels?.arGuidanceInitializing ?? 'Inicializando...';
          } else if (info.stage === 'ready') {
            arStatus = 'ready';
            guidanceText = labels?.arGuidanceReady ?? 'Modelo pronto. Aponte para a peca.';
            modelReady = true;
            if (OnnxInference.isUsingFallback?.()) {
              showFallbackWarning = true;
              window.ArOverlay?.showWarning('Modo offline: inferencia via servidor');
            }
          } else if (info.stage === 'fallback') {
            arStatus = 'ready';
            guidanceText = labels?.arGuidanceFallback ?? 'Modo servidor ativo';
            modelReady = true;
            showFallbackWarning = true;
            window.ArOverlay?.showWarning('Usando servidor para deteccao (conexao necessaria)');
          }
        }
      });
    } catch (err: any) {
      arStatus = 'ready';
      guidanceText = labels?.arGuidanceFallback ?? 'Usando servidor remoto';
      modelReady = true;
      showFallbackWarning = true;
      window.ArOverlay?.showError('model_failed', err?.message ?? 'Falha ao carregar modelo de IA. Verifique sua conexao.');
    }
  }

  function stopArMode() {
    stopScanLoop();
    try { ArCamera.stopCamera(); } catch {}
  }

  async function handleCapture() {
    const video = document.getElementById('camera-video') as HTMLVideoElement;
    if (!video || isCapturing) return;
    isCapturing = true;
    const c = document.createElement('canvas');
    c.width = video.videoWidth;
    c.height = video.videoHeight;
    c.getContext('2d')!.drawImage(video, 0, 0);
    const blob = await new Promise<Blob | null>(r => c.toBlob(r, 'image/jpeg', 0.9));
    if (blob) session.setFile(new File([blob], 'capture.jpg', { type: 'image/jpeg' }));
    isCapturing = false;
  }

  async function handleAnalyze() {
    if (!session.selectedFile) return;
    const formData = new FormData();
    formData.append('image', session.selectedFile);
    const v = session.vehicle;
    if (v?.make) formData.append('vehicle_make', v.make);
    if (v?.model) formData.append('vehicle_model', v.model);
    if (v?.year) formData.append('vehicle_year', v.year);
    try {
      const res = await fetch('/predict', { method: 'POST', body: formData });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const data = await res.json();
      if (data.matches && data.matches.length > 0) {
        session.setResults({
          category: data.product_id ?? data.category ?? '',
          confidence: data.confidence ?? 0,
          scores: data.all_scores ?? {},
          matches: (data.matches ?? []).map((m: Record<string, unknown>) => ({
            id: m.id ?? '',
            category: m.category ?? '',
            name: m.title ?? '',
            imageUrl: m.image_url,
            pricePix: m.price,
            priceInstallment: m.price,
            installmentCount: 6,
            sourceUrl: m.link,
          })),
        });
        stopArMode();
        navigateTo('results');
        window.ArOverlay?.showSuccess('Produto encontrado!');
      } else {
        window.ArOverlay?.showInfo('Nenhum produto encontrado. Tente outra imagem.');
      }
    } catch (err: any) {
      guidanceText = labels?.arGuidanceError ?? 'Erro de conexao.';
      if (err?.message === 'Failed to fetch' || err?.message?.startsWith('HTTP')) {
        window.ArOverlay?.showWarning('Modo offline: verificando conexao com servidor.');
      } else {
        window.ArOverlay?.showError('network_error', 'Erro de rede. Verifique sua conexao.');
      }
    }
  }

  function handleStop() {
    stopArMode();
    navigateTo('upload');
  }

  $effect(() => {
    startArMode();
    return () => stopArMode();
  });
</script>

<div id="ar-screen" class="screen">
  <div class="ar-viewport">
    <video id="camera-video" autoplay playsinline muted></video>
    <canvas id="detection-canvas" style="display:none;"></canvas>

    {#if import.meta.env.DEV}
    <div style="position:absolute;top:4px;left:4px;z-index:999;background:rgba(0,0,0,0.85);color:#0f0;font:10px monospace;padding:4px 6px;border-radius:4px;max-width:95%;pointer-events:none;white-space:pre-wrap;">
AR:{arStatus} scan:{scanPhase} model:{modelReady?'OK':'NO'}
pipe:{dbgPipelineState}
frames:{dbgFrames} raw:{dbgLastRaw||'--'}
det:{currentDetection?currentDetection.category+' '+(currentDetection.confidence*100).toFixed(0)+'%':'none'}
zones:{detectionZones.length} scores:{Object.keys(allScores).length}
{#if Object.keys(allScores).length > 0}{Object.entries(allScores).sort((a,b) => (b[1] as number) - (a[1] as number)).slice(0,3).map(([k,v]) => k+':'+ ((v as number)*100).toFixed(0)+'%').join(' | ')}{/if}
    </div>
    {/if}

    {#if showFallbackWarning}
    <div class="ar-fallback-banner">
      <span class="ar-fallback-icon">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-2.83 0z"/>
          <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
        </svg>
      </span>
      <span class="ar-fallback-text">Modo degradado: inferencia via servidor (mais lento). Verifique sua conexao.</span>
      <button class="ar-fallback-dismiss" onclick={() => showFallbackWarning = false} aria-label="Dismiss">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
      </button>
    </div>
    {/if}

    {#if detectionZones.length > 0}
    <svg class="ar-zones-overlay"
         style="position:absolute;top:0;left:0;width:100%;height:100%;cursor:crosshair;z-index:10;"
         role="button"
         tabindex="0"
         onclick={handleZonesSvgClick}
         onkeydown={(e) => e.key === 'Enter' && handleZonesSvgClick(e as any)}>
      {#each detectionZones as zone, i}
        <g style="cursor:pointer; opacity: {zoneStale(zone.zoneIndex) ? 0.45 : 1};">
          <rect
            x={zone.svgX + 2} y={zone.svgY + 2}
            width={zone.svgW - 4} height={zone.svgH - 4}
            fill={selectedZoneIndex === i ? 'rgba(34,197,94,0.15)' : 'rgba(59,130,246,0.08)'}
            stroke={selectedZoneIndex === i ? '#22c55e' : '#3b82f6'}
            stroke-width={selectedZoneIndex === i ? 2.5 : 1.5}
            rx="6" />
          <text
            x={zone.svgX + zone.svgW / 2} y={zone.svgY + 18}
            text-anchor="middle" font-size="11" font-weight="bold"
            fill={selectedZoneIndex === i ? '#86efac' : '#93c5fd'}>
            {brandStore.translateCategory(zone.className)}
          </text>
          <text
            x={zone.svgX + zone.svgW / 2} y={zone.svgY + 32}
            text-anchor="middle" font-size="9"
            fill={selectedZoneIndex === i ? '#86efac' : '#93c5fd'} opacity="0.8">
            {(zone.confidence * 100).toFixed(0)}%
          </text>
        </g>
      {/each}
    </svg>
    {:else if arStatus === 'scanning' || arStatus === 'ready' || scanPhase === 'scanning'}
    <svg style="position:absolute;top:0;left:0;width:100%;height:100%;pointer-events:none;z-index:10;" opacity="0.15">
      <line x1="33.33%" y1="0%" x2="33.33%" y2="100%" stroke="#60a5fa" stroke-width="1"/>
      <line x1="66.67%" y1="0%" x2="66.67%" y2="100%" stroke="#60a5fa" stroke-width="1"/>
      <line x1="0%" y1="33.33%" x2="100%" y2="33.33%" stroke="#60a5fa" stroke-width="1"/>
      <line x1="0%" y1="66.67%" x2="100%" y2="66.67%" stroke="#60a5fa" stroke-width="1"/>
    </svg>
    {/if}

    {#if scanPhase === 'detected' && detectionZones.length > 0}
    <div class="ar-zones-bottom-sheet">
      <div class="ar-sheet-indicator"></div>
      <div class="ar-sheet-header">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10"/><path d="M12 8v4m0 4h.01"/>
        </svg>
        <span class="ar-sheet-title">Encontramos {detectionZones.length} peça{detectionZones.length > 1 ? 's' : ''}!</span>
        {#if noDetectionCountdown > 0}
          <span class="ar-sheet-timer">— reavaliando em {noDetectionCountdown}s</span>
        {/if}
      </div>
      <p class="ar-sheet-hint">Toque em uma peça para selecionar ou aguarde a seleção automática.</p>
      <div class="ar-sheet-zones">
        {#each detectionZones as zone, i}
          <button
            class="ar-sheet-zone-btn"
            class:selected={selectedZoneIndex === i}
            type="button"
            onclick={() => selectZoneFromSheet(i)}>
            <span class="ar-sheet-zone-icon">
              {brandStore.categories.find(c => c.id === zone.className)?.icon ?? ''}
            </span>
            <span class="ar-sheet-zone-name">{brandStore.translateCategory(zone.className)}</span>
            <span class="ar-sheet-zone-conf">{(zone.confidence * 100).toFixed(0)}%</span>
          </button>
        {/each}
      </div>
    </div>
    {/if}

    <div class="ar-hud-overlay">
      <div class="ar-status-bar">
        <div class="ar-status-item">
          <span style="color: var(--text-muted); font-size: 10px;">{labels?.arLightLabel ?? 'LUZ'}</span>
          <div class="ar-light-indicator">
            {#each [1,2,3,4,5] as level}
              <div class="ar-light-segment" class:active={level <= lightLevel && brightness >= 0.47} class:warning={level <= lightLevel && brightness >= 0.24 && brightness < 0.47} class:low={level <= lightLevel && brightness < 0.24}></div>
            {/each}
          </div>
        </div>
        <div class="ar-model-status"
          class:status-scanning={scanPhase === 'scanning' || scanPhase === 'detected'}
          class:status-found={scanPhase === 'selected' || arStatus === 'found'}>
          {scanPhase === 'scanning' ? (labels?.arStatusDetecting ?? 'DETECTANDO...') :
           scanPhase === 'detected' ? 'PEÇAS ENCONTRADAS' :
           scanPhase === 'selected' ? 'SELECIONADO' :
           arStatus === 'loading' ? (labels?.arStatusLoading ?? 'LOADING') :
           arStatus === 'ready' ? (labels?.arStatusReady ?? 'READY') :
           arStatus === 'scanning' ? (labels?.arStatusDetecting ?? 'DETECTANDO...') :
           arStatus === 'found' ? (labels?.arStatusLocked ?? 'LOCKED') :
           arStatus === 'error' ? (labels?.arStatusFallback ?? 'FALLBACK') :
           (labels?.arStatusDefault ?? '--')}
        </div>
        <button
          class="ar-audio-toggle"
          type="button"
          onclick={() => { audioEnabled = !audioEnabled; setAudioEnabled(audioEnabled); }}
          aria-label={audioEnabled ? 'Desativar som' : 'Ativar som'}>
          {#if audioEnabled}
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
              <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"/>
            </svg>
          {:else}
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
              <line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/>
            </svg>
          {/if}
        </button>
      </div>

      <ArScanLine active={scanPhase === 'scanning' || scanPhase === 'detected' || arStatus === 'ready' || arStatus === 'scanning'} />
      <ArCornerBrackets
        detected={!!currentDetection || detectionZones.length > 0 || selectedZoneIndex !== null}
        highlightIntensity={highlightIntensity} />

      {#if selectedZone && scanPhase === 'selected'}
      <div class="ar-floating-card">
        <div class="ar-floating-card-header">
          <span class="ar-floating-icon">
            {brandStore.categories.find(c => c.id === selectedZone.className)?.icon ?? ''}
          </span>
          <div class="ar-floating-info">
            <span class="ar-floating-category">{brandStore.translateCategory(selectedZone.className)}</span>
            <span class="ar-floating-confidence">{(selectedZone.confidence * 100).toFixed(0)}% confiança</span>
          </div>
        </div>
        <div class="ar-floating-actions">
          <button class="btn btn-primary btn-go" type="button" disabled={isCapturing} onclick={goToPart}>
            {isCapturing ? 'Enviando...' : 'Ir para peça'}
          </button>
          <button class="btn btn-outline btn-choose" type="button" onclick={chooseAnother}>
            Escolher outra
          </button>
        </div>
      </div>
      {/if}

      {#if currentDetection}
      <div class="ar-watermark">
        <span class="ar-watermark-brand">{labels?.watermarkBrand ?? 'AutoZ'}</span>
        <span class="ar-watermark-sub">{labels?.watermarkAi ?? 'Identificado por IA'}</span>
      </div>
      {/if}

      <ArDataTag
        visible={!!currentDetection || detectionZones.length > 0}
        category={currentDetection ? brandStore.translateCategory(currentDetection.category) : ''}
        confidence={currentDetection?.confidence ?? 0}
        status={currentDetection && currentDetection.confidence >= 0.6 ? 'high' : currentDetection && currentDetection.confidence >= 0.4 ? 'medium' : 'low'}
        zones={detectionZones.map(z => ({ name: z.className, confidence: z.confidence, status: scoreColor(z.confidence) as 'high' | 'medium' | 'low' }))} />

      {#if arStatus !== 'idle' && topScores.length > 0}
      <div class="ar-scores-overlay">
        <div class="ar-scores-title">Top {topScores.length}</div>
        {#each topScores as [cat, conf]}
          <div class="ar-score-row">
            <span class="ar-score-label">{brandStore.translateCategory(cat)}</span>
            <div class="ar-score-bar">
              <div class="ar-score-fill {scoreColor(conf)}" style="width: {conf * 100}%"></div>
            </div>
            <span class="ar-score-pct">{(conf * 100).toFixed(0)}%</span>
          </div>
        {/each}
      </div>
      {/if}
    </div>
  </div>

  <div class="ar-controls">
    <div class="ar-guidance">{guidanceText}</div>

    {#if scanPhase === 'selected' && selectedZone}
    <div class="ar-selected-banner">
      <span class="ar-selected-icon">
        {brandStore.categories.find(c => c.id === selectedZone?.className)?.icon ?? ''}
      </span>
      <div class="ar-selected-info">
        <span class="ar-selected-name">{brandStore.translateCategory(selectedZone.className)}</span>
        <span class="ar-selected-conf">{(selectedZone.confidence * 100).toFixed(0)}% confiança</span>
      </div>
    </div>
    {/if}

    {#if arStatus === 'found' && currentDetection}
    <div class="ar-found-banner">
      <span class="ar-found-icon">{brandStore.categories.find(c => c.id === currentDetection?.category)?.icon ?? ''}</span>
      <div class="ar-found-info">
        <span class="ar-found-name">{brandStore.translateCategory(currentDetection.category)}</span>
        <span class="ar-found-conf">{(currentDetection.confidence * 100).toFixed(0)}% confianca</span>
      </div>
    </div>
    {/if}

    <div class="ar-controls-row">
      <button class="btn btn-capture" type="button" disabled={isCapturing || arStatus === 'loading'} onclick={handleCapture}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
          <circle cx="12" cy="13" r="4"/>
        </svg>
        {labels?.btnCapture ?? 'Capturar'}
      </button>
      <button class="btn btn-scan"
        class:active-scan={scanPhase === 'scanning' || scanPhase === 'detected' || scanPhase === 'selected'}
        type="button"
        disabled={arStatus === 'loading' || arStatus === 'error'}
        onclick={toggleScan}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          {#if scanPhase === 'scanning' || scanPhase === 'detected' || scanPhase === 'selected'}
            <rect x="6" y="6" width="12" height="12" rx="1"/>
          {:else}
            <path d="M3 7V5a2 2 0 0 1 2-2h2M17 3h2a2 2 0 0 1 2 2v2M21 17v2a2 2 0 0 1-2 2h-2M7 21H5a2 2 0 0 1-2-2v-2"/>
            <line x1="7" y1="12" x2="17" y2="12"/>
          {/if}
        </svg>
        {scanPhase === 'scanning' || scanPhase === 'detected' || scanPhase === 'selected' ? 'Parar' : 'Escanear Peça'}
      </button>
      <button class="btn btn-analyze" class:btn-analyze-pulse={arStatus === 'found'} type="button" disabled={!session.selectedFile} onclick={handleAnalyze}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="11" cy="11" r="8"/>
          <line x1="21" y1="21" x2="16.65" y2="16.65"/>
        </svg>
        {labels?.btnAnalyze ?? 'Analisar'}
      </button>
    </div>
    <button class="btn btn-stop" type="button" onclick={handleStop}>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
        <rect x="6" y="6" width="12" height="12" rx="1"/>
      </svg>
      {labels?.btnStop ?? 'Encerrar Scanner'}
    </button>
  </div>
</div>

<style>
  .ar-zones-overlay { pointer-events: auto; }

  .ar-zones-bottom-sheet {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: rgba(10, 10, 20, 0.95);
    border-top: 1px solid rgba(59, 130, 246, 0.3);
    border-radius: 20px 20px 0 0;
    padding: 12px 20px 28px;
    z-index: 20;
    backdrop-filter: blur(16px);
    animation: sheetRise 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
  }

  @keyframes sheetRise {
    from { transform: translateY(100%); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }

  .ar-sheet-indicator {
    width: 40px; height: 4px;
    background: rgba(255,255,255,0.15);
    border-radius: 2px;
    margin: 0 auto 14px;
  }

  .ar-sheet-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 4px;
    color: #93c5fd;
    font-weight: 700;
    font-size: 15px;
  }

  .ar-sheet-title { flex-shrink: 0; }

  .ar-sheet-timer {
    font-size: 12px;
    font-weight: 400;
    color: #64748b;
    flex-shrink: 0;
  }

  .ar-sheet-hint {
    font-size: 12px;
    color: #64748b;
    margin: 0 0 14px;
  }

  .ar-sheet-zones {
    display: flex;
    gap: 10px;
    overflow-x: auto;
    padding-bottom: 4px;
    scrollbar-width: thin;
    scrollbar-color: rgba(59,130,246,0.4) transparent;
  }

  .ar-sheet-zone-btn {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 5px;
    flex-shrink: 0;
    background: rgba(59, 130, 246, 0.1);
    border: 1px solid rgba(59, 130, 246, 0.3);
    border-radius: 12px;
    padding: 10px 16px;
    min-width: 84px;
    cursor: pointer;
    transition: all 0.2s;
  }

  .ar-sheet-zone-btn.selected {
    background: rgba(34, 197, 94, 0.15);
    border-color: #22c55e;
  }

  .ar-sheet-zone-btn:hover:not(.selected) { border-color: rgba(59,130,246,0.6); }

  .ar-sheet-zone-icon { font-size: 22px; line-height: 1; }

  .ar-sheet-zone-name {
    font-size: 12px;
    font-weight: 600;
    color: #cbd5e1;
    text-align: center;
    white-space: nowrap;
  }

  .ar-sheet-zone-conf {
    font-size: 11px;
    color: #64748b;
  }

  .ar-floating-card {
    position: absolute;
    bottom: 90px;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(10, 10, 20, 0.93);
    border: 1px solid rgba(34, 197, 94, 0.5);
    border-radius: 16px;
    padding: 16px 20px;
    min-width: 290px;
    max-width: 340px;
    width: calc(100% - 32px);
    z-index: 25;
    backdrop-filter: blur(12px);
    animation: cardReveal 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  }

  @keyframes cardReveal {
    from { opacity: 0; transform: translateX(-50%) translateY(20px); }
    to { opacity: 1; transform: translateX(-50%) translateY(0); }
  }

  .ar-floating-card-header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 14px;
  }

  .ar-floating-icon { font-size: 28px; line-height: 1; }

  .ar-floating-info { display: flex; flex-direction: column; gap: 2px; }

  .ar-floating-category { color: white; font-size: 16px; font-weight: 700; }

  .ar-floating-confidence { color: #86efac; font-size: 13px; font-weight: 500; }

  .ar-floating-actions { display: flex; flex-direction: column; gap: 8px; }

  .btn-go {
    width: 100%;
    background: #22c55e;
    color: #052e16;
    font-weight: 700;
    border-radius: 10px;
    padding: 12px 16px;
    font-size: 15px;
    border: none;
    cursor: pointer;
    transition: background 0.2s;
  }

  .btn-go:hover:not(:disabled) { background: #16a34a; }
  .btn-go:disabled { opacity: 0.5; cursor: not-allowed; }

  .btn-choose {
    width: 100%;
    background: transparent;
    color: #94a3b8;
    font-weight: 500;
    border-radius: 10px;
    padding: 10px 16px;
    font-size: 14px;
    border: 1px solid rgba(148, 163, 184, 0.25);
    cursor: pointer;
    transition: all 0.2s;
  }

  .btn-choose:hover { border-color: rgba(148, 163, 184, 0.5); color: white; }

  .ar-selected-banner {
    display: flex;
    align-items: center;
    gap: 10px;
    background: rgba(34, 197, 94, 0.12);
    border: 1px solid rgba(34, 197, 94, 0.3);
    border-radius: 12px;
    padding: 10px 14px;
    margin-bottom: 10px;
  }

  .ar-selected-icon { font-size: 22px; line-height: 1; }

  .ar-selected-info { display: flex; flex-direction: column; gap: 1px; }

  .ar-selected-name { color: white; font-size: 14px; font-weight: 700; }

  .ar-selected-conf { color: #86efac; font-size: 12px; }

  .btn-scan {
    flex: 1;
    background: #3b82f6;
    color: white;
    font-weight: 700;
    border-radius: 12px;
    padding: 14px 16px;
    font-size: 14px;
    border: none;
    cursor: pointer;
    transition: background 0.2s, transform 0.1s, box-shadow 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
  }

  .btn-scan:hover:not(:disabled) { background: #2563eb; }
  .btn-scan:active:not(:disabled) { transform: scale(0.97); }
  .btn-scan:disabled { opacity: 0.45; cursor: not-allowed; }

  .btn-scan.active-scan {
    background: rgba(30, 58, 95, 0.9);
    color: #60a5fa;
    border: 1px solid rgba(96, 165, 250, 0.4);
    animation: pulseScan 1.5s infinite;
  }

  @keyframes pulseScan {
    0%, 100% { box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.5); }
    50% { box-shadow: 0 0 0 8px rgba(59, 130, 246, 0); }
  }

  .status-scanning {
    color: #fbbf24 !important;
    animation: blink 1s infinite;
  }

  .status-found { color: #22c55e !important; }

  @keyframes blink {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }

  .ar-controls-row {
    display: flex;
    gap: 10px;
    align-items: center;
  }
</style>
